import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { baseEndpoint } from "../../api/endpoints";
import { getToken } from "../../api/getToken";
import {
  Door,
  DoorDataPayload,
  DoorState,
} from "../../interfaces/doors";
import { createAppAsyncThunk } from "../hooks";
import type { RootState } from "../store";

type RequestState = "pending" | "fulfilled" | "rejected";

type DoorsStore = {
  byFloor: Record<string, DoorState[]>;
  websocketOpenByFloor: Record<string, boolean | undefined>;
  liveRevisions: {
    floors: Record<string, number | undefined>;
    doors: Record<string, Record<string, number | undefined> | undefined>;
  };
  requests: {
    statuses: Record<string, RequestState | undefined>;
    errors: Record<string, string | undefined>;
    activeRequestIDs: Record<string, string | undefined>;
    baselineRevisions: Record<string, number | undefined>;
  };
};

type HydrateDoorsPayload = {
  floorID: string;
  doors: Door[];
};

type DoorsResponse = {
  success?: boolean;
  payload?: Door[] | null;
  title?: string;
};

const emptyDoors: DoorState[] = [];
const eventHistoryLimit = 32;

const initialState: DoorsStore = {
  byFloor: {},
  websocketOpenByFloor: {},
  liveRevisions: {
    floors: {},
    doors: {},
  },
  requests: {
    statuses: {},
    errors: {},
    activeRequestIDs: {},
    baselineRevisions: {},
  },
};

export const getDoors = createAppAsyncThunk<Door[], { floorID: string }>(
  "doors/getDoors",
  async ({ floorID }, { rejectWithValue }) => {
    try {
      const token = await getToken();
      const response = await fetch(
        `${baseEndpoint}/doors?floorID=${encodeURIComponent(floorID)}`,
        {
          headers: { authorization: token },
        },
      );
      const data = (await response.json()) as DoorsResponse;

      if (response.ok && data.success) {
        return Array.isArray(data.payload) ? data.payload : [];
      }

      return rejectWithValue(data.title || "Failed to get door occupancy");
    } catch (error) {
      return rejectWithValue(
        error instanceof Error
          ? error.message
          : "Failed to get door occupancy",
      );
    }
  },
);

function doorIdentity(door: Pick<Door, "id" | "deviceSN">): string {
  return door.id || door.deviceSN;
}

function doorRevisionKey(door: Pick<Door, "id" | "deviceSN">): string {
  return door.deviceSN || door.id;
}

function doorsMatch(
  current: Pick<Door, "id" | "deviceSN">,
  incoming: Pick<Door, "id" | "deviceSN">,
): boolean {
  return (
    doorIdentity(current) === doorIdentity(incoming) ||
    Boolean(current.deviceSN && current.deviceSN === incoming.deviceSN)
  );
}

function parseTimestamp(value?: string): number | null {
  if (!value) {
    return null;
  }

  const timestamp = Date.parse(value);
  return Number.isNaN(timestamp) ? null : timestamp;
}

function snapshotTimestamp(door: DoorState): number | null {
  const candidates = [
    door.current?.timeEnd,
    door.timestamp,
    door.eventTime,
    door.current?.lastEventAt,
  ];

  for (const candidate of candidates) {
    const timestamp = parseTimestamp(candidate);
    if (timestamp !== null) {
      return timestamp;
    }
  }

  return null;
}

function compareSequences(left?: string, right?: string): number | null {
  if (!left || !right || !/^\d+$/.test(left) || !/^\d+$/.test(right)) {
    return null;
  }

  try {
    const leftSequence = BigInt(left);
    const rightSequence = BigInt(right);
    if (leftSequence === rightSequence) {
      return 0;
    }
    return leftSequence > rightSequence ? 1 : -1;
  } catch {
    return null;
  }
}

function snapshotRegresses(current: DoorState, incoming: DoorState): boolean {
  if (!current.current || !incoming.current) {
    return false;
  }

  const currentStart = parseTimestamp(current.current.timeStart);
  const incomingStart = parseTimestamp(incoming.current.timeStart);

  if (currentStart !== null && incomingStart !== null) {
    if (incomingStart < currentStart) {
      return true;
    }
    if (incomingStart > currentStart) {
      return false;
    }
  }

  return (
    incoming.current.totalIn < current.current.totalIn ||
    incoming.current.totalOut < current.current.totalOut
  );
}

function snapshotIsOlder(current: DoorState, incoming: DoorState): boolean {
  const currentTimestamp = snapshotTimestamp(current);
  const incomingTimestamp = snapshotTimestamp(incoming);

  return (
    currentTimestamp !== null &&
    incomingTimestamp !== null &&
    incomingTimestamp < currentTimestamp
  );
}

function shouldApplyDoorUpdate(
  current: DoorState,
  incoming: DoorState,
): boolean {
  if (
    incoming.eventID &&
    (current.eventID === incoming.eventID ||
      current.acceptedEventIDs?.includes(incoming.eventID))
  ) {
    return false;
  }

  const sequenceOrder = compareSequences(incoming.sequence, current.sequence);
  if (sequenceOrder !== null) {
    return (
      sequenceOrder > 0 &&
      !snapshotRegresses(current, incoming) &&
      !snapshotIsOlder(current, incoming)
    );
  }

  if (snapshotRegresses(current, incoming)) {
    return false;
  }

  const currentTimestamp = snapshotTimestamp(current);
  const incomingTimestamp = snapshotTimestamp(incoming);
  if (currentTimestamp !== null && incomingTimestamp !== null) {
    return incomingTimestamp >= currentTimestamp;
  }

  // If either side has no comparable clock, prefer the event that just arrived.
  return true;
}

function mergeDoor(current: DoorState, incoming: DoorState): DoorState {
  const acceptedEventIDs = incoming.eventID
    ? [...(current.acceptedEventIDs || []), incoming.eventID].slice(
        -eventHistoryLimit,
      )
    : current.acceptedEventIDs;

  return {
    ...current,
    ...incoming,
    eventID: incoming.eventID || current.eventID,
    reportType: incoming.reportType || current.reportType,
    eventTime: incoming.eventTime || current.eventTime,
    deltaIn: incoming.deltaIn ?? current.deltaIn,
    deltaOut: incoming.deltaOut ?? current.deltaOut,
    sequence: incoming.sequence || current.sequence,
    acceptedEventIDs,
  };
}

function initialiseDoor(incoming: DoorState): DoorState {
  return incoming.eventID
    ? { ...incoming, acceptedEventIDs: [incoming.eventID] }
    : incoming;
}

function mergeDoorList(
  currentDoors: DoorState[],
  incomingDoors: DoorState[],
): DoorState[] {
  const merged = [...currentDoors];

  incomingDoors.forEach((incoming) => {
    const index = merged.findIndex((current) => doorsMatch(current, incoming));

    if (index === -1) {
      merged.push(initialiseDoor(incoming));
      return;
    }

    if (shouldApplyDoorUpdate(merged[index], incoming)) {
      merged[index] = mergeDoor(merged[index], incoming);
    }
  });

  return merged.sort((left, right) => left.id.localeCompare(right.id));
}

function doorUpdateWillApply(
  currentDoors: DoorState[],
  incoming: DoorState,
): boolean {
  const current = currentDoors.find((door) => doorsMatch(door, incoming));
  return !current || shouldApplyDoorUpdate(current, incoming);
}

function reconcileDoorList(
  currentDoors: DoorState[],
  incomingDoors: DoorState[],
  liveDoorRevisions: Record<string, number | undefined>,
  baselineRevision: number,
): DoorState[] {
  const receivedDuringRequest = (door: DoorState) =>
    (liveDoorRevisions[doorRevisionKey(door)] || 0) > baselineRevision;
  const retained = currentDoors.filter((current) => {
    const remainsAssigned = incomingDoors.some((incoming) =>
      doorsMatch(current, incoming),
    );
    return remainsAssigned || receivedDuringRequest(current);
  });
  const mergeableIncoming = incomingDoors.filter((incoming) => {
    const current = currentDoors.find((door) => doorsMatch(door, incoming));
    return !current || !receivedDuringRequest(current);
  });

  return mergeDoorList(retained, mergeableIncoming);
}

const doorsSlice = createSlice({
  name: "doors",
  initialState,
  reducers: {
    hydrateDoors: (state, action: PayloadAction<HydrateDoorsPayload>) => {
      const { floorID, doors } = action.payload;
      state.byFloor[floorID] = mergeDoorList(
        state.byFloor[floorID] || [],
        doors,
      );
    },
    updateDoorData: (state, action: PayloadAction<DoorDataPayload>) => {
      const incoming = action.payload;
      const currentDoors = state.byFloor[incoming.floorID] || [];
      if (doorUpdateWillApply(currentDoors, incoming)) {
        state.byFloor[incoming.floorID] = mergeDoorList(currentDoors, [
          incoming,
        ]);
        const revision =
          (state.liveRevisions.floors[incoming.floorID] || 0) + 1;
        state.liveRevisions.floors[incoming.floorID] = revision;
        const doorRevisions =
          state.liveRevisions.doors[incoming.floorID] || {};
        doorRevisions[doorRevisionKey(incoming)] = revision;
        state.liveRevisions.doors[incoming.floorID] = doorRevisions;
      }
    },
    setDoorWebsocketOpen: (
      state,
      action: PayloadAction<{ floorID: string; isOpen: boolean }>,
    ) => {
      state.websocketOpenByFloor[action.payload.floorID] =
        action.payload.isOpen;
    },
    resetGetDoorsStatus: (
      state,
      action: PayloadAction<{ floorID: string }>,
    ) => {
      state.requests.statuses[action.payload.floorID] = undefined;
      state.requests.errors[action.payload.floorID] = undefined;
      state.requests.activeRequestIDs[action.payload.floorID] = undefined;
      state.requests.baselineRevisions[action.payload.floorID] = undefined;
    },
  },
  extraReducers(builder) {
    builder.addCase(getDoors.pending, (state, action) => {
      const floorID = action.meta.arg.floorID;
      state.requests.statuses[floorID] = "pending";
      state.requests.errors[floorID] = undefined;
      state.requests.activeRequestIDs[floorID] = action.meta.requestId;
      state.requests.baselineRevisions[floorID] =
        state.liveRevisions.floors[floorID] || 0;
    });
    builder.addCase(getDoors.rejected, (state, action) => {
      const floorID = action.meta.arg.floorID;
      if (state.requests.activeRequestIDs[floorID] !== action.meta.requestId) {
        return;
      }
      state.requests.statuses[floorID] = "rejected";
      state.requests.errors[floorID] =
        (typeof action.payload === "string" && action.payload) ||
        action.error.message ||
        "Failed to get door occupancy";
      state.requests.activeRequestIDs[floorID] = undefined;
      state.requests.baselineRevisions[floorID] = undefined;
    });
    builder.addCase(getDoors.fulfilled, (state, action) => {
      const floorID = action.meta.arg.floorID;
      if (state.requests.activeRequestIDs[floorID] !== action.meta.requestId) {
        return;
      }

      const baselineRevision =
        state.requests.baselineRevisions[floorID] || 0;
      const liveDoorRevisions = state.liveRevisions.doors[floorID] || {};
      state.byFloor[floorID] = reconcileDoorList(
        state.byFloor[floorID] || [],
        action.payload,
        liveDoorRevisions,
        baselineRevision,
      );

      const retainedRevisionKeys = new Set(
        state.byFloor[floorID].map(doorRevisionKey),
      );
      Object.keys(liveDoorRevisions).forEach((key) => {
        if (!retainedRevisionKeys.has(key)) {
          delete liveDoorRevisions[key];
        }
      });
      state.requests.statuses[floorID] = "fulfilled";
      state.requests.errors[floorID] = undefined;
      state.requests.activeRequestIDs[floorID] = undefined;
      state.requests.baselineRevisions[floorID] = undefined;
    });
  },
});

export const selectDoorsForFloor = (state: RootState, floorID: string) =>
  state.doors.byFloor[floorID] || emptyDoors;

export const selectGetDoorsStatus = (state: RootState, floorID: string) =>
  state.doors.requests.statuses[floorID];

export const selectGetDoorsError = (state: RootState, floorID: string) =>
  state.doors.requests.errors[floorID];

export const selectDoorWebsocketOpen = (
  state: RootState,
  floorID: string,
) => Boolean(state.doors.websocketOpenByFloor[floorID]);

export const {
  hydrateDoors,
  updateDoorData,
  setDoorWebsocketOpen,
  resetGetDoorsStatus,
} = doorsSlice.actions;

export default doorsSlice.reducer;
